import { LineItem } from './lineitem-details.component';
import { Input, Output, EventEmitter, Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { Subject, Observable, defer, Subscription } from 'rxjs';
import { AbstractDetailsComponent } from '../../shared/abstract-details.component';
import { ActivatedRoute } from '../../../../node_modules/@angular/router';
import { NgForm, ControlContainer, FormGroup, FormControl, Validators } from '../../../../node_modules/@angular/forms';
import { QuickeditService } from '../../../services/quickedit.service';
import { ClipboardService } from '../../../../node_modules/ngx-clipboard';
import { QueryParameter } from '../../../interfaces/queryString';
import { SapiQueryResponse } from '../../../services/sapi.service';
import { debounceTime } from 'rxjs/operators';
import { TableRow } from '../../../services/bb-table.service';

@Component({
	selector: 'lineitem-quickedit',
	templateUrl: './lineitem-quickedit.component.html',
	styleUrls: ['./lineitem-quickedit.component.scss'],
	viewProviders: [{
		provide: ControlContainer,
		useExisting: NgForm
	}]
})

export class LineitemQuickeditComponent extends AbstractDetailsComponent implements OnInit, OnDestroy {
	@Input() parentForm: NgForm;

	@Input() get row() {
		return this._row;
	}
	set row(value) {
		this._row = value;
	}

	@Input() isNew: boolean;
	@Input() unlink: boolean;
	@Input() noQuickeditService: boolean;

	@Input() index: number | string;

	@Output() saved: EventEmitter<any> = new EventEmitter<any>();
	@Output() removed: EventEmitter<any> = new EventEmitter<any>();
	@Output() unlinked: EventEmitter<any> = new EventEmitter<any>();
	@Output() canceled: EventEmitter<any> = new EventEmitter<any>();
	@Output() rowChange = new EventEmitter();

	_row: TableRow;
	get lineitem() {
		return this.entity as LineItem;
	}

	label = 'LINE_ITEM';
	saveAction: Observable<any>;

	disabled: boolean;

	subscriptions: Subscription;

	date: Date;
	fields: string[];

	formGroup: FormGroup;
	formGroupName: string;

	constructor(protected route: ActivatedRoute,
		protected quickeditService: QuickeditService,
		protected clipboardService: ClipboardService,
		protected location: Location) {
		super(route, location);

		this.entityConfiguration = {
			name: 'lineitem',
			entityTranslation: this.translate.instant('LINE_ITEM'),
			standAlone: true,
			parentPath: '/adunits/lineitems',
			basePath: '/adunits/lineitems/'
		};

		this.disabled = true;

		this.subscriptions = new Subscription();

		this.fields = ['status', 'title', 'code', 'timeout', 'vast_url'];

		this.subscriptions = new Subscription();

		this.date = new Date();

		this.formGroupName = 'lineitem-quickedit-' + this.date.getTime();

		this.formGroup = new FormGroup({});

		this.formGroup.addControl('title', new FormControl('', { validators: Validators.required }));

		this.fields.forEach((formFieldName: string) => {
			if (formFieldName !== 'title') {
				this.formGroup.addControl(formFieldName, new FormControl(''));
			}
		});
	}

	ngOnInit() {
		if (this.parentForm) {
			this.parentForm.form.addControl(this.formGroupName, this.formGroup);
		}

		if (this.row && this.row.data) {
			this.entity = this.row.data;
		}
		super.ngOnInit();

		if (this.noQuickeditService === undefined || !this.noQuickeditService) {
			this.saveAction = defer(() => this.save(true));
			this.quickeditService.addSaveAction(this.saveAction);
		}

		this.fields.forEach((fieldName: string) => {
			if (this.entity && this.entity.hasOwnProperty(fieldName)) {
				const formControl: FormControl = this.formGroup.get(fieldName) as FormControl;
				if (fieldName === 'code' && this.entity.id) {
					formControl.disable();
				}
				formControl.setValue(this.entity[fieldName]);
			}
		});

		this.subscriptions.add(
			this.formGroup.get('title').valueChanges.subscribe((title: string) => {
				this.titleUpdated(title);
			})
		);

		this.subscriptions.add(
			this.formGroup.get('code').valueChanges.pipe(
				debounceTime(400)
			).subscribe((code: string) => {
				const formControl: FormControl = this.formGroup.get('code') as FormControl;
				if (formControl.value !== code) {
					this.codeUpdated(code);
				}
			})
		);

		this.subscriptions.add(
			this.formGroup.valueChanges.pipe(
				debounceTime(400)
			).subscribe((fields: any) => {
				this._row.data = { ...this._row.data, ...fields };
				this.entity = this._row.data;
				this.rowChange.emit(this._row);
			})
		);
	}

	ngOnDestroy() {
		if (this.parentForm && this.parentForm.form) {
			this.parentForm.form.removeControl(this.formGroupName);
		}
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	saveQuickEdit() {
		this.save(true).subscribe(() => {
			this.removeSaveAction();
		});
	}

	save(force: boolean): Observable<any> {
		const saveSubject = super.save(force, null, false);

		saveSubject.subscribe((response) => {
			if (this.isNew) {
				this.row.id = this.entity.id;
				this.row.data = this.entity;
				this.saved.emit(true);
			}
			this.row.quickEditing = false;
			this.resetEntity();
		});
		return saveSubject;
	}

	removeSaveAction() {
		if (this.saveAction) {
			this.quickeditService.removeSaveAction(this.saveAction);
		}
	}

	delete(): Subject<void> {
		if (this.unlink) {
			this.removeSaveAction();
			this.unlinked.emit();
		} else {
			const deleteSubject = super.delete();

			deleteSubject.subscribe((response) => {
				this.removed.emit();
			});
			this.isNew = true;
			return deleteSubject;
		}
	}

	resetEntity() {
		this.removeSaveAction();
		this.cancel().subscribe((entity) => {
			this.row.data = entity;
			this.entity = entity;
		});
	}

	onCancelClick() {
		if (this.isNew) {
			this.canceled.emit(true);
			this.removeSaveAction();
		} else {
			this.resetEntity();
			this.row.quickEditing = false;
		}
	}

	titleUpdated(title: string) {
		this.row.data.title = title;
		this.codeUpdated(title);
	}

	codeUpdated(code: string) {
		if (!this.row.id) {
			this.row.data.code = code ? code.replace(/-/g, '*').replace(/[^\w\-]{1,}/g, '_').toLowerCase() : '';

			const formControl: FormControl = this.formGroup.get('code') as FormControl;
			formControl.setValue(this.row.data.code, { emitEvent: false });

			const queryParams: QueryParameter[] = [{ name: 'q', values: ['code:' + this.row.data.code] }];
			this.sapiService.query('lineitem', queryParams).subscribe((sapiQueryResponse: SapiQueryResponse) => {
				if (sapiQueryResponse && sapiQueryResponse.totalAmount > 0) {
					formControl.setErrors({ 'not-unique': true });
					formControl.markAsTouched();
					formControl.markAsDirty();
				}
			});
		}
	}

	getVastUrl() {
		return encodeURIComponent(this.lineitem.vast_url);
	}

	copyVastUrlToClipboard() {
		this.clipboardService.copyFromContent(this.lineitem.vast_url);
		this.overlayService.createToast('Copied to clipboard', 'clipboard');
	}
}
